import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listPrintProducts, listPrintProviders } from '../../../services/adminApi';
import ProductDrawer from './ProductDrawer';
import { Tag } from './ui';
import { etatProduit, formatEuros } from './productForm';
import './catalog.css';

// Produits et formats : un format Bookipix relie a UN fournisseur, une reference et un prix. C'est ICI, et nulle part ailleurs,
// que se reglent coût, marge, prix de vente et port d'un produit — les formats historiques Gelato compris.
// Le prix montre au client vient toujours de ces fiches, jamais du fournisseur en direct.

const TON_ETAT = { off: 'mute', testers: 'warn', public: 'ok' };

export default function ProductsSection() {
  const { t } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const [produits, setProduits] = useState(null);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [ouvert, setOuvert] = useState(null); // { produit } (produit null = nouveau)

  const charger = useCallback(async () => {
    try {
      const [p, f] = await Promise.all([listPrintProducts(), listPrintProviders()]);
      setProduits(p.products);
      setFournisseurs(f.providers);
      setErreur('');
      return p.products;
    } catch (err) {
      setErreur(err?.message || t('catalog.products.loadFailed'));
      return null;
    }
  }, [t]);

  useEffect(() => { charger(); }, [charger]);

  // Un lien depuis « Tarifs et livraison » (?produit=standard) ouvre directement la fiche.
  const voulu = params.get('produit');
  useEffect(() => {
    if (!voulu || !produits || ouvert) return;
    const trouve = produits.find((p) => p.formatId === voulu);
    if (trouve) setOuvert({ produit: trouve });
  }, [voulu, produits, ouvert]);

  const fermer = () => {
    setOuvert(null);
    if (params.get('produit')) { params.delete('produit'); setParams(params, { replace: true }); }
  };

  const apresEnregistrement = async (texte) => {
    await charger();
    fermer();
    setMessage(texte);
  };

  if (erreur && !produits) return <p className="adm-error" role="alert">{erreur}</p>;
  if (!produits) return <p className="adm-state" role="status">{t('catalog.common.loading')}</p>;

  const fournisseurDe = (id) => (fournisseurs.find((f) => f.id === id) || {}).label || id;

  return (
    <>
      <div className="adm-toolbar">
        <p className="adm-note">{t('catalog.products.note')}</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { setMessage(''); setOuvert({ produit: null }); }}>{t('catalog.products.add')}</button>
      </div>
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}

      <div className="adm-tbl">
        <table>
          <thead>
            <tr>
              <th>{t('catalog.products.cols.format')}</th>
              <th>{t('catalog.products.cols.provider')}</th>
              <th>{t('catalog.products.cols.page')}</th>
              <th className="num">{t('catalog.products.cols.cost')}</th>
              <th className="num">{t('catalog.products.cols.margin')}</th>
              <th className="num">{t('catalog.products.cols.price')}</th>
              <th>{t('catalog.products.cols.state')}</th>
            </tr>
          </thead>
          <tbody>
            {produits.map((p) => {
              const etat = etatProduit(p);
              const marge = Number.isInteger(p.priceBaseCents) && Number.isInteger(p.costBaseCents) ? p.priceBaseCents - p.costBaseCents : null;
              return (
                <tr key={p.formatId} className="is-open" onClick={() => { setMessage(''); setOuvert({ produit: p }); }}>
                  <td>
                    <button type="button" className="adm-rowbtn" onClick={(e) => { e.stopPropagation(); setMessage(''); setOuvert({ produit: p }); }}>
                      <b>{p.name.fr || p.formatId}</b>
                    </button>
                    <small>{p.coverType === 'hard' ? t('catalog.drawer.coverHard') : t('catalog.drawer.coverSoft')} · {p.formatId}{p.builtin ? ` · ${t('catalog.products.builtin')}` : ''}</small>
                  </td>
                  <td>{fournisseurDe(p.providerId)}</td>
                  <td>{p.trimWidthMm} × {p.trimHeightMm} mm</td>
                  <td className="num">{formatEuros(p.costBaseCents)}</td>
                  <td className="num">{marge === null ? '—' : formatEuros(marge)}</td>
                  <td className="num"><b>{formatEuros(p.priceBaseCents)}</b>{Number.isInteger(p.pricePer2PagesCents) && <small>+ {formatEuros(p.pricePer2PagesCents)} {t('catalog.products.per2')}</small>}</td>
                  <td><Tag tone={TON_ETAT[etat]}>{t(`catalog.states.${etat}`)}</Tag></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="adm-note">{t('catalog.products.footnote')}</p>

      {ouvert && (
        <ProductDrawer
          key={ouvert.produit ? ouvert.produit.formatId : 'nouveau'}
          produit={ouvert.produit}
          fournisseurs={fournisseurs}
          onClose={fermer}
          onSaved={apresEnregistrement}
        />
      )}
    </>
  );
}
